import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {PromotedTypeView, PromotedTypeSection} from '@/types/types'

export const PROMOTED_TYPES_QUERY_KEY = ['types', 'promoted'] as const

export const PROMOTABLE_ENTITY_TABLES = {
    PARTY_TYPES: 'party_types',
    IDENTITY_TYPES: 'identity_types',
    SYSTEM_TYPES: 'system_types',
    RESOURCE_TYPES: 'resource_types',
    FUNCTION_GROUP_TYPES: 'function_group_types',
} as const

export const PROMOTED_TESTID_PREFIX = 'promoted-'

/**
 * Path segment that uniquely identifies a promoted by-type route. Used by
 * static parent sidebar items as {@code excludePathContains} so the parent
 * yields to the more specific promoted entry (issue 680).
 */
export const BY_TYPE_PATH_SEGMENT = '/by-type/'

const LOCALE_EN = 'en'
const LOCALE_DE = 'de'
const LANG_PREFIX_DE = 'de'

export interface UsePromotedTypesResult {
    promoted: PromotedTypeView[]
    isLoading: boolean
}

export function usePromotedTypes(): UsePromotedTypesResult {
    const services = useServices()
    const {data, isLoading} = useQuery({
        queryKey: PROMOTED_TYPES_QUERY_KEY,
        queryFn: () => services.types.promoted(),
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    })
    return {promoted: data ?? [], isLoading}
}

export function promotedFor(promoted: PromotedTypeView[], section: PromotedTypeSection): PromotedTypeView[] {
    return promoted.filter((p) => p.parentSection === section)
}

export function findPromoted(
    promoted: PromotedTypeView[],
    entityTable: string,
    code: string,
): PromotedTypeView | undefined {
    return promoted.find((p) => p.entityTable === entityTable && p.code === code)
}

/**
 * Resolve the per-locale label for a promoted entry. Falls back to English then
 * to the raw type code when the requested locale is missing.
 */
export function pickPromotedLabel(entry: PromotedTypeView, lang: string): string {
    const normalized = lang.startsWith(LANG_PREFIX_DE) ? LOCALE_DE : LOCALE_EN
    return entry.label[normalized] ?? entry.label[LOCALE_EN] ?? entry.code
}
